/**
 * @file 学员列表筛选项
 * @description RequestTable searchForm 表单项；配合展开 / 收起。
 * 教材下拉按已选科目调 `/common/textbook/ddl` 联动。
 *
 * @whenToUse 仅学员列表 RequestTable。
 * @whenNotToUse 其他业务筛选。
 */

import { useEffect, useState } from 'react'
import { fetchTextbookDdl } from '@/api/textbook.ts'
import { DateRangePicker } from '@/components/ui/date-range-picker.tsx'
import { GradeSelect } from '@/components/ui/grade-select.tsx'
import { Input } from '@/components/ui/input.tsx'
import { InputNumber } from '@/components/ui/input-number.tsx'
import { Select } from '@/components/ui/select.tsx'
import type { RequestTableSearchContext } from '@/components/ui/request-table.tsx'
import {
  LEARNING_LEVEL_OPTIONS,
  SEMESTER_OPTIONS,
  STAGE_OPTIONS,
  STUDENT_STATUS_LABEL,
  SUBJECT_OPTIONS,
  type StudentQuery,
} from '@/components/pages/student-page/types.ts'

const STATUS_OPTIONS = [
  { value: '', label: '全部' },
  ...Object.entries(STUDENT_STATUS_LABEL).map(([value, label]) => ({
    value,
    label,
  })),
]

/**
 * 学员筛选项内部实现：教材选项随科目联动
 */
function StudentSearchFieldsInner({
  values,
  setField,
  expanded,
}: RequestTableSearchContext<StudentQuery>) {
  const [textbookOptions, setTextbookOptions] = useState<
    { value: string; label: string }[]
  >([])

  useEffect(() => {
    let active = true
    void fetchTextbookDdl(
      values.subject ? { subject: values.subject } : { pageSize: 200 },
    )
      .then((rows) => {
        if (!active) return
        setTextbookOptions(rows.map((row) => ({ value: row.id, label: row.name })))
      })
      .catch(() => {
        if (active) setTextbookOptions([])
      })
    return () => {
      active = false
    }
  }, [values.subject])

  return (
    <>
        <Input
          value={values.studentName}
          placeholder="学员名称"
          onChange={(event) => setField('studentName', event.target.value)}
        />
        <Select
          value={values.status}
          placeholder="状态"
          options={STATUS_OPTIONS}
          onChange={(value) => setField('status', value)}
        />
        <DateRangePicker
          value={values.createTimeRange}
          placeholder="创建时间"
          onChange={(value) => setField('createTimeRange', value)}
        />
      {expanded ? (
        <>
            <Select
              value={values.subject}
              placeholder="科目"
              options={SUBJECT_OPTIONS}
              onChange={(value) => {
                setField('subject', value)
                // 科目变化后教材选项随之刷新，清空已选教材
                setField('textbook', '')
              }}
            />
            <Select
              value={values.textbook}
              placeholder="教材"
              options={textbookOptions}
              onChange={(value) => setField('textbook', value)}
            />
            <Select
              value={values.educationStage}
              placeholder="阶段"
              options={STAGE_OPTIONS}
              onChange={(value) => setField('educationStage', value)}
            />
            <GradeSelect
              value={values.grade}
              stage={values.educationStage}
              allowEmpty
              placeholder="年级"
              onChange={(value) => setField('grade', value)}
            />
            <Select
              value={values.semester}
              placeholder="学期"
              options={SEMESTER_OPTIONS}
              onChange={(value) => setField('semester', value)}
            />
            <Select
              value={values.learningGrade}
              placeholder="学习力等级"
              options={LEARNING_LEVEL_OPTIONS}
              onChange={(value) => setField('learningGrade', value)}
            />
            <InputNumber
              value={values.learningForce}
              min={0}
              placeholder="学习力"
              onChange={(value) => setField('learningForce', value)}
            />
        </>
      ) : null}
    </>
  )
}

/**
 * 学员列表筛选项（基础三项常显，其余随 expanded 展开）
 * @param ctx RequestTable 搜索上下文
 */
export function StudentSearchFields(ctx: RequestTableSearchContext<StudentQuery>) {
  return <StudentSearchFieldsInner {...ctx} />
}
